import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import './styles/Modal.css';
import Calculator from './components/Calculator';
import Auth from './components/Auth';
import HomePage from './components/HomePage';
import ShopPage from './pages/ShopPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'; 
import TermsOfServicePage from './pages/TermsOfServicePage'; 
import AboutUsPage from './pages/AboutUsPage'; // Import the new About Us page
import AccountPage from './pages/AccountPage'; // Import the new Account page
import ScrollToTop from './components/ScrollToTop';
import { useAuth } from './contexts/AuthContext';
import { checkSubscription } from './utils/subscriptionUtils';
import { calculateSectionCompletionStatus } from './utils/sectionCompletionUtils';
import DashboardLayout from './components/layout/DashboardLayout';
import ProjectManager from './components/ProjectManager';
import ProAccessPrompt from './components/ProAccessPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import { DebugProvider } from './contexts/DebugContext';
import FeedbackButton from './components/feedback/FeedbackButton'; // Added import

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Preview route component for showing limited functionality to non-logged-in users
const PreviewRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  // Pass the preview flag to the children
  return React.cloneElement(children, { isPreview: !user, user: user });
};

// Define ProjectManagerModal outside of AppContent and memoize it
const ProjectManagerModal = React.memo(({
  show,
  user, // Keep user prop to ensure it only renders when user exists
  calculatorState,
  onLoadProject,
  onProjectAction,
  isPro,
  showSaveFormInitially,
  onClose,
  onProjectUpdated // Add new prop for the modal definition
}) => {
  // Debug log inside the new component
  // console.log('[ProjectManagerModal DEBUG] Render:', { show, userExists: !!user });

  if (!show || !user) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content project-manager-modal">
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>Project Management (Beta Feature)</h2>
        <p className="project-manager-info">Save your current cookie calculations or load a previously saved project.</p>
        <ProjectManager 
          calculatorState={calculatorState}
          onLoadProject={onLoadProject}
          onProjectAction={onProjectAction}
          isPro={isPro}
          showSaveFormInitially={showSaveFormInitially}
          onClose={onClose} 
          onProjectUpdated={onProjectUpdated} // Pass it down to the actual ProjectManager
        />
      </div>
    </div>
  );
});

function AppContent() {
  const { user, loading, error: authError } = useAuth();
  const location = useLocation(); // Get location object
  const navigate = useNavigate(); // Get navigate function
  
  // Initialize dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to true (dark)
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : true;
  });

  // Effect to apply the dark mode class to the body and save preference
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    // Save the user's preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]); // Re-run only when isDarkMode changes
  
  // Track calculator version
  const [calculatorVersion, setCalculatorVersion] = useState(() => {
    return localStorage.getItem('calculatorVersion') || 'simple';
  });
  
  // Unit states - FIXED: Inches (isMetric=false) and Grams (isMetricWeight=true)
  const isMetric = false; // Always inches
  const isMetricWeight = true; // Always grams
  
  // Active section state for Pro Calculator
  const [activeSection, setActiveSection] = useState('reference');
  
  // Project management state
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  
  // Add event listener for Escape key to close the ProjectManager modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showProjectManager) {
        setShowProjectManager(false);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    
    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showProjectManager]);
  
  // State persistence for calculator inputs
  const [basicCalculatorState, setBasicCalculatorState] = useState({});
  const [proCalculatorState, setProCalculatorState] = useState({}); // Initialize Pro state
  
  // Manually track section completion status for Pro Calculator
  const [sectionCompletionStatus, setSectionCompletionStatus] = useState({});

  // Load calculator states from localStorage on initial mount
  useEffect(() => {
    const savedProState = localStorage.getItem('proCalculatorState');
    console.log('[App DEBUG] Attempting to load pro state from localStorage. Found:', savedProState ? 'Yes' : 'No', 'Raw value:', savedProState);
    
    if (savedProState && savedProState !== "undefined") { // Check for null AND the literal string "undefined"
      try {
        const parsedProState = JSON.parse(savedProState);
        // Ensure parsedProState is a valid object before setting
        if (parsedProState && typeof parsedProState === 'object') {
          console.log('[App DEBUG] Successfully parsed pro state from localStorage:', JSON.stringify(parsedProState));
          setProCalculatorState(parsedProState);
        } else {
          console.error('[App DEBUG] Parsed proCalculatorState from localStorage is not a valid object:', parsedProState);
          localStorage.removeItem('proCalculatorState'); // Remove corrupted item
          setProCalculatorState({}); // Reset to default
        }
      } catch (error) {
        console.error('[App DEBUG] Error parsing seemingly valid proCalculatorState string from localStorage:', error);
        localStorage.removeItem('proCalculatorState'); // Remove corrupted item on other parse errors too
        setProCalculatorState({}); // Reset to default
      }
    } else if (savedProState === "undefined") {
      // Handle the specific case where the localStorage item IS the string "undefined"
      console.warn('[App DEBUG] proCalculatorState in localStorage was the string "undefined". Clearing it and resetting to default state.');
      localStorage.removeItem('proCalculatorState');
      setProCalculatorState({}); // Reset to default empty state
    }
    // If savedProState is null (no item found), proCalculatorState remains its initial {} (empty object), which is fine.

    // Assuming basicCalculatorState is also persisted and uses 'basicCalculatorState' key
    const savedBasicState = localStorage.getItem('basicCalculatorState'); 
    if (savedBasicState) {
      try {
        const parsedBasicState = JSON.parse(savedBasicState);
        setBasicCalculatorState(parsedBasicState);
        console.log('[App DEBUG] Loaded basicCalculatorState from localStorage on mount.');
      } catch (error) {
        console.error('[App DEBUG] Error parsing basicCalculatorState from localStorage:', error);
        // localStorage.removeItem('basicCalculatorState'); // Optional: clear corrupted item
      }
    }
  }, []); // Empty dependency array: runs only once on mount

  // Callback for ProCalculator to update its state in App.js
  const handleProCalculatorStateChange = useCallback((newState) => {
    console.log('[App DEBUG] handleProCalculatorStateChange called from context. New state:', JSON.stringify(newState));
    setProCalculatorState(newState); 
    localStorage.setItem('proCalculatorState', JSON.stringify(newState));

    // Directly calculate and set section status with the state from context
    const newStatusFromContext = calculateSectionCompletionStatus(newState || {});
    console.log('[App DEBUG] Status calculated directly in handleProCalculatorStateChange:', JSON.stringify(newStatusFromContext));
    
    // Compare with current App-level sectionCompletionStatus before setting
    // to avoid unnecessary re-renders if the status hasn't changed.
    const currentAppStatusString = JSON.stringify(sectionCompletionStatus);
    const newStatusFromContextString = JSON.stringify(newStatusFromContext);

    if (currentAppStatusString !== newStatusFromContextString) {
      console.log('[App DEBUG] Directly UPDATING sectionCompletionStatus from handleProCalculatorStateChange.');
      setSectionCompletionStatus(newStatusFromContext);
    } else {
      console.log('[App DEBUG] Directly NOT UPDATING sectionCompletionStatus from handleProCalculatorStateChange (strings match).');
    }

  }, [setProCalculatorState, sectionCompletionStatus]); // MODIFIED: Added sectionCompletionStatus to dependency array

  const handleBasicCalculatorStateChange = useCallback((newState) => {
    setBasicCalculatorState(newState); // Update the state holder
  }, []);

  // Calculate section completion status based on the proCalculatorState
  const calculatedSectionCompletionStatus = useMemo(() => {
    console.log('[App DEBUG] useMemo calculating section status with pro state:', JSON.stringify(proCalculatorState)); // Log input state
    const newStatus = calculateSectionCompletionStatus(proCalculatorState || {});
    console.log('[App DEBUG] useMemo calculated new status:', JSON.stringify(newStatus)); // Log output status
    return newStatus;
  }, [proCalculatorState]);

  // Update the state variable only when the calculated value actually changes
  useEffect(() => {
    const currentStatusString = JSON.stringify(sectionCompletionStatus);
    const newStatusString = JSON.stringify(calculatedSectionCompletionStatus);
    console.log('[App DEBUG] useEffect comparing section status. Current:', currentStatusString, 'New Calculated:', newStatusString); // Log comparison strings
    if (currentStatusString !== newStatusString) {
      console.log('[App DEBUG] useEffect UPDATING sectionCompletionStatus state.'); // Log if update happens
      setSectionCompletionStatus(calculatedSectionCompletionStatus);
    } else {
      console.log('[App DEBUG] useEffect NOT updating sectionCompletionStatus state (strings match).'); // Log if update doesn't happen
    }
  }, [calculatedSectionCompletionStatus]); // MODIFIED DEPENDENCY ARRAY

  // Memoized callbacks for ProjectManager
  const handleLoadProject = useCallback((loadedState) => {
    if (calculatorVersion === 'simple') {
      setBasicCalculatorState(loadedState);
    } else {
      setProCalculatorState(loadedState);
    }
    setShowProjectManager(false); // Close modal on load
  }, [calculatorVersion, setBasicCalculatorState, setProCalculatorState, setShowProjectManager]);

  const handleProjectAction = useCallback((action, message) => {
    // Placeholder for notifications or other actions
    // console.log(`[AppContent] Project action: ${action}, Message: ${message}`);
  }, []); // No dependencies from AppContent scope needed for this example

  const handleProjectManagerClose = useCallback(() => {
    console.log('[App DEBUG] handleProjectManagerClose called'); // DEBUG LOG
    setShowProjectManager(false);
    setShowSaveForm(false); // Also reset showSaveForm state
  }, [setShowProjectManager, setShowSaveForm]);

  const handleProjectUpdatedSuccessfully = useCallback(() => {
    handleProjectManagerClose();
    navigate('/pro');
    // Consider if active section needs to be 'Results'
    // setActiveSection('results'); // This would require passing setActiveSection down or another mechanism
  }, [navigate, handleProjectManagerClose]);

  // For demo purposes, let's add some buttons to toggle section status
  const toggleSectionStatus = (sectionId) => {
    setSectionCompletionStatus(prev => {
      const currentStatus = prev[sectionId];
      let newStatus;
      
      if (currentStatus === 'neutral') newStatus = 'incomplete';
      else if (currentStatus === 'incomplete') newStatus = 'completed';
      else newStatus = 'neutral';
      
      return {
        ...prev,
        [sectionId]: newStatus
      };
    });
  };
  
  // Update calculator version when localStorage changes
  useEffect(() => {
    localStorage.setItem('calculatorVersion', calculatorVersion);
  }, [calculatorVersion]);
  
  // Initialize section completion status when the component mounts
  useEffect(() => {
    // Set initial status for testing - we'll set reference to incomplete
    // updateSectionStatus('reference', 'incomplete');
    // updateSectionStatus('cookieNames', 'neutral');
    // updateSectionStatus('dimensions', 'neutral');
    // updateSectionStatus('sets', 'neutral');
    // updateSectionStatus('colors', 'neutral');
    // updateSectionStatus('orders', 'neutral');
    // updateSectionStatus('results', 'neutral');
    // updateSectionStatus('all', 'neutral');
  }, []);

  // Effect to synchronize calculatorVersion with the current route
  useEffect(() => {
    // Check for Pro Calculator route
    if (location.pathname === '/pro') { // Correct path for Pro
      if (calculatorVersion !== 'pro') {
        console.log('[App DEBUG] Syncing calculatorVersion to pro based on route (/pro).');
        setCalculatorVersion('pro');
      }
    // Check for Simple Calculator routes (adjust paths if needed)
    } else if (location.pathname === '/calculator' || location.pathname === '/') { 
      if (calculatorVersion !== 'simple') {
        console.log('[App DEBUG] Syncing calculatorVersion to simple based on route (e.g., /calculator or /).');
        setCalculatorVersion('simple');
      }
    }
    // Not touching calculatorVersion if on other pages like /account, /shop etc.
  }, [location.pathname, calculatorVersion, setCalculatorVersion]);

  // Show loading indicator while checking auth status
  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">Loading authentication...</div>
      </div>
    );
  }
  
  // Determine if we're showing the pro calculator
  const isProCalculator = calculatorVersion === 'pro';
  
  // Define sections for the Pro Calculator
  const sections = [
    { id: 'reference', label: 'Reference Cookie' },
    { id: 'cookieNames', label: 'Cookie Names' },
    { id: 'sets', label: 'Sets' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'colors', label: 'Color %' },
    { id: 'orders', label: 'Orders' },
    { id: 'results', label: 'Results' }
    // 'all' section removed as requested
  ];
  
  return (
    <div className={`App ${isDarkMode ? 'dark-mode' : ''}`}>
      <ScrollToTop />
      <FeedbackButton /> {/* Added FeedbackButton */}
      <Routes>
        <Route path="/login" element={!user ? <Auth /> : <Navigate to="/" />} />
        
        <Route path="/settings" element={
          <ProtectedRoute>
            <DashboardLayout 
              isProCalculator={isProCalculator}
              sections={sections}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              onSaveProject={(e) => {
                e.stopPropagation();
                setShowProjectManager(true);
                setShowSaveForm(true);
              }}
              onManageProjects={(e) => {
                e.stopPropagation();
                setShowProjectManager(true);
              }}
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
              calculatorVersion={calculatorVersion}
              setCalculatorVersion={setCalculatorVersion}
              sectionCompletionStatus={sectionCompletionStatus}
            >
              <div className="settings-page">
                <h1>Settings</h1>
                <div className="settings-card">
                  <h2>Appearance</h2>
                  <div className="setting-item dark-mode-setting">
                    <div className="setting-label-group">
                      <label>Dark Mode</label>
                      <span className="setting-description">Switch between light and dark theme</span>
                    </div>
                    <div className="ios-toggle-wrapper">
                      <input 
                        type="checkbox" 
                        checked={isDarkMode} 
                        onChange={(e) => setIsDarkMode(e.target.checked)}
                        id="dark-mode-toggle"
                        className="ios-toggle"
                      />
                      <label htmlFor="dark-mode-toggle" className="ios-toggle-label">
                        <span className="ios-toggle-inner">
                          <span className="ios-toggle-icon light">☀️</span>
                          <span className="ios-toggle-icon dark">🌙</span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        {/* Shop Page */}
        <Route path="/shop" element={
          <DashboardLayout
            isProCalculator={false} // Shop page is not part of the pro calculator flow
            sections={sections} // Pass sections for sidebar consistency if needed, or filter them
            activeSection={null} // No specific pro calc section is active
            setActiveSection={() => {}} // No pro calc section to set
            onSaveProject={(e) => {
              e.stopPropagation();
              setShowProjectManager(true);
              setShowSaveForm(true);
            }}
            onManageProjects={(e) => {
              e.stopPropagation();
              setShowProjectManager(true);
            }}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion={calculatorVersion} // Or a general version if preferred
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
          >
            <ShopPage />
          </DashboardLayout>
        } />
        
        {/* Home page at root */}
        <Route path="/" element={
          <DashboardLayout 
            isProCalculator={false}
            sections={sections}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            onSaveProject={(e) => {
              e.stopPropagation();
              setShowProjectManager(true);
              setShowSaveForm(true);
            }}
            onManageProjects={(e) => {
              e.stopPropagation();
              setShowProjectManager(true);
            }}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion="simple"
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
          >
            <HomePage />
          </DashboardLayout>
        } />
        
        {/* Basic Calculator */}
        <Route path="/basic" element={
          <PreviewRoute>
            <DashboardLayout 
              isProCalculator={false}
              sections={sections}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              onSaveProject={(e) => {
                e.stopPropagation();
                setShowProjectManager(true);
                setShowSaveForm(true);
              }}
              onManageProjects={(e) => {
                e.stopPropagation();
                setShowProjectManager(true);
              }}
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
              calculatorVersion="simple"
              setCalculatorVersion={setCalculatorVersion}
              sectionCompletionStatus={sectionCompletionStatus}
            >
              <Calculator 
                isPro={false} 
                isDarkMode={isDarkMode} 
                setIsDarkMode={setIsDarkMode}
                savedState={basicCalculatorState}
                onStateChange={handleBasicCalculatorStateChange}
              />
            </DashboardLayout>
          </PreviewRoute>
        } />
        
        {/* Pro Calculator */}
        <Route path="/pro" element={
          loading ? (
            <div className="loading-screen">Loading...</div>
          ) : (
            <ErrorBoundary>
              <DashboardLayout 
                isProCalculator={true}
                sections={sections}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                onSaveProject={(e) => {
                  e.stopPropagation();
                  setShowProjectManager(true);
                  setShowSaveForm(true);
                }}
                onManageProjects={(e) => {
                  e.stopPropagation();
                  setShowProjectManager(true);
                }}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion="pro"
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
              >
                <Calculator 
                  isPro={true} 
                  isDarkMode={isDarkMode} 
                  setIsDarkMode={setIsDarkMode}
                  calculatorVersion="pro"
                  setCalculatorVersion={setCalculatorVersion}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  showProjectManager={showProjectManager}
                  setShowProjectManager={setShowProjectManager}
                  showSaveForm={showSaveForm}
                  setShowSaveForm={setShowSaveForm}
                  savedState={proCalculatorState} 
                  onStateChange={handleProCalculatorStateChange} 
                />
              </DashboardLayout>
            </ErrorBoundary>
          )
        } />
        
        {/* Account Page Route - Protected */}
        <Route 
          path="/account" 
          element={
            <ProtectedRoute>
              <DashboardLayout
                isProCalculator={false} // Or determine based on context if needed
                sections={sections} // Pass sections for sidebar consistency
                activeSection={null} // No pro calc section active
                setActiveSection={() => {}} // No pro calc section to set
                onSaveProject={(e) => {
                  e.stopPropagation();
                  setShowProjectManager(true);
                  setShowSaveForm(true);
                }}
                onManageProjects={(e) => {
                  e.stopPropagation();
                  setShowProjectManager(true);
                }}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AccountPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/about-us" element={
          <DashboardLayout
            isProCalculator={false} // Not part of pro calculator flow
            sections={sections} // Pass sections for sidebar consistency
            activeSection={null} // No pro calc section active
            setActiveSection={() => {}} // No pro calc section to set
            onSaveProject={(e) => {
              e.stopPropagation();
              setShowProjectManager(true);
              setShowSaveForm(true);
            }}
            onManageProjects={(e) => {
              e.stopPropagation();
              setShowProjectManager(true);
            }}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion={calculatorVersion}
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
          >
            <AboutUsPage />
          </DashboardLayout>
        } />
        
        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {/* Project manager modal - accessible from anywhere */}
      {console.log('[App DEBUG] Rendering ProjectManagerModal. Current proCalculatorState:', proCalculatorState, 'calculatorVersion:', calculatorVersion, 'basicCalculatorState:', basicCalculatorState)}
      <ProjectManagerModal
        show={showProjectManager}
        user={user}
        calculatorState={calculatorVersion === 'simple' ? basicCalculatorState : proCalculatorState}
        onLoadProject={handleLoadProject}
        onProjectAction={handleProjectAction}
        isPro={isProCalculator}
        showSaveFormInitially={showSaveForm}
        onClose={handleProjectManagerClose}
        onProjectUpdated={handleProjectUpdatedSuccessfully} // Pass the new handler here
      />
    </div>
  );
}

// Check if Supabase is properly configured
const isSupabaseConfigured = () => {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return url && key && url !== 'https://your-project-id.supabase.co' && key !== 'your-anon-key';
};

function App() {
  return (
    <DebugProvider>
      <AppContent />
    </DebugProvider>
  );
}

export default App;
